// 複数辞書の管理。レジストリ＝kv['dicts'] = [{ id, name, source, enabled, count, license, addedAt }]。
// エントリは 'dict' ストアに [dictId, 見出し小文字] = 訳[] の複合キーで保存（辞書ごとに分離＝個別削除可）。
// 引く側(dict.js)は enabledDicts() の全辞書を横断して集め、辞書ごとに束ねて返す。
import { db, kvGet, kvSet } from './db.js'

// 辞書は開発者が配る共有・読み取り専用データ＝R2の公開バケットから鍵なしで fetch する。
// 全ユーザー共通の1つなのでコードに埋め込む（公開URL・秘密ではない）。
// ※公開バケットには PWA 公開URL からの CORS 許可が必要（pwa/scripts/set-cors.mjs 等）。
const DICT_BASE = 'https://pub-3e118dceb6ed42a2adad922998c2b5bc.r2.dev'

const REG = 'dicts'
const newId = () => 'dict-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 7)

export async function listDicts() {
  const v = await kvGet(REG)
  return Array.isArray(v) ? v : []
}
async function saveReg(arr) {
  await kvSet(REG, arr)
}
export async function enabledDicts() {
  return (await listDicts()).filter((d) => d.enabled !== false)
}
export async function setDictEnabled(id, on) {
  const arr = await listDicts()
  const d = arr.find((x) => x.id === id)
  if (!d) return
  d.enabled = !!on
  await saveReg(arr)
}
export async function renameDict(id, name) {
  const arr = await listDicts()
  const d = arr.find((x) => x.id === id)
  if (!d) return
  d.name = name
  await saveReg(arr)
}

// 辞書削除：レジストリ＋その辞書の全エントリ（[id, *] 範囲）を消す。
export async function deleteDict(id) {
  await saveReg((await listDicts()).filter((x) => x.id !== id))
  const d = await db()
  const tx = d.transaction('dict', 'readwrite')
  const range = IDBKeyRange.bound([id], [id, []]) // [id]〜[id,空配列) ＝ [id, *] 全部
  let cur = await tx.store.openCursor(range)
  while (cur) { cur.delete(); cur = await cur.continue() }
  await tx.done
}

// NDJSON ストリームを辞書として取り込む。1行 = ["見出し", ["訳",...]]。逐次でメモリを食わない。
export async function installFromStream(stream, info, onProgress) {
  const id = newId()
  const d = await db()
  const reader = stream.getReader()
  const dec = new TextDecoder('utf-8')
  let buf = '', loaded = 0, count = 0, batch = []
  const BATCH = 5000
  const flush = async () => {
    if (!batch.length) return
    const tx = d.transaction('dict', 'readwrite')
    for (const [head, entries] of batch) tx.store.put(entries, [id, head])
    await tx.done
    count += batch.length
    batch = []
  }
  const push = (line) => {
    try {
      const [h, e] = JSON.parse(line)
      if (h) batch.push([String(h).toLowerCase(), Array.isArray(e) ? e : [String(e)]])
    } catch { /* 壊れた行は飛ばす */ }
  }
  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    loaded += value.byteLength
    buf += dec.decode(value, { stream: true })
    let nl
    while ((nl = buf.indexOf('\n')) >= 0) {
      const line = buf.slice(0, nl); buf = buf.slice(nl + 1)
      if (line) push(line)
      if (batch.length >= BATCH) await flush()
    }
    if (onProgress) onProgress({ loaded, total: info.total || 0, count, done: false })
  }
  if (buf.trim()) push(buf)
  await flush()
  const meta = {
    id, name: info.name || '(辞書)', source: info.source || 'import',
    license: info.license || '', enabled: true, count, addedAt: new Date().toISOString(),
  }
  const arr = await listDicts(); arr.push(meta); await saveReg(arr)
  if (onProgress) onProgress({ loaded, total: info.total || 0, count, done: true })
  return meta
}

// 端末のファイルから取り込み。NDJSON/JSONL はそのまま、TSV/TXT は「語\t意味」を変換して流す。
export async function installFromFile(file, name) {
  const lower = file.name.toLowerCase()
  if (lower.endsWith('.tsv') || lower.endsWith('.txt')) {
    const text = await file.text()
    const map = new Map()
    for (const line of text.split('\n')) {
      const tab = line.indexOf('\t')
      if (tab < 0) continue
      const key = line.slice(0, tab).trim().toLowerCase()
      if (!key) continue
      const senses = line.slice(tab + 1).split(/ \/ |；|;/).map((s) => s.trim()).filter(Boolean)
      if (!senses.length) continue
      let set = map.get(key); if (!set) map.set(key, (set = new Set()))
      for (const s of senses) set.add(s)
    }
    const ndjson = [...map].map(([k, set]) => JSON.stringify([k, [...set]])).join('\n')
    return installFromStream(new Blob([ndjson]).stream(), { name: name || file.name, source: 'import', total: ndjson.length })
  }
  return installFromStream(file.stream(), { name: name || file.name.replace(/\.[^.]+$/, ''), source: 'import', total: file.size })
}

// クラウド配布カタログ（dict/catalog.json）。{ dicts:[{ id,name,key,size,license,lang }] }。
// 公開バケットから鍵なしで取得（CORS 許可が前提）。
export async function fetchDictCatalog() {
  try {
    const r = await fetch(DICT_BASE + '/dict/catalog.json', { cache: 'no-cache' })
    if (!r.ok) return []
    const j = await r.json()
    return Array.isArray(j.dicts) ? j.dicts : Array.isArray(j) ? j : []
  } catch {
    return []
  }
}

// カタログ項目をダウンロードして取り込む（公開URLから直接ストリーム）。
export async function downloadCloudDict(entry, onProgress) {
  const r = await fetch(DICT_BASE + '/' + entry.key)
  if (!r.ok) throw new Error('辞書のダウンロードに失敗: ' + r.status)
  const total = Number(r.headers.get('content-length')) || entry.size || 0
  return installFromStream(r.body, { name: entry.name, source: 'cloud', license: entry.license, total }, onProgress)
}
